'use client';

import { locationCookie } from '@/constants/settings';
import { LayoutContext } from '@/contexts/LayoutContext';
import { IGeoLocation } from '@/lib/geoLocation';
import { LayoutReducerActionType } from '@/types/layout';
import { getCookie, setCookie } from '@lamesarv-sdk/tools';
import { useContext, useEffect } from 'react';

export const HandlerGeoLocation = () => {
  const { dispatch } = useContext(LayoutContext);

  const updateGeoLocation = (location: IGeoLocation) => {
    dispatch({
      type: LayoutReducerActionType.updateGeoLocation,
      payload: [location.latitude, location.longitude],
    });
  };

  const getLocationByApi = async () => {
    try {
      const sessionLocation = getCookie(locationCookie);

      if (sessionLocation) {
        updateGeoLocation(JSON.parse(sessionLocation));
        return;
      }

      const response = await fetch('/api/geoLocation');
      const data: IGeoLocation | { disabled: boolean } = await response.json();

      if (!(data as { disabled: boolean }).disabled) {
        updateGeoLocation(data as IGeoLocation);
        setCookie(locationCookie, JSON.stringify(data));
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({
        type: LayoutReducerActionType.updateGeoLocationLoading,
        payload: false,
      });
    }
  };

  const processGeoLocation = () => {
    if (!navigator.geolocation) {
      getLocationByApi();
      return;
    }

    // This is for 1st time users, if they don't choose any option
    // so, it won't show the loading spinner forever
    const timeout = setTimeout(() => {
      dispatch({
        type: LayoutReducerActionType.updateGeoLocationLoading,
        payload: false,
      });
    }, 100);

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        if (permissionStatus.state === 'granted') clearTimeout(timeout);
      });
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeout);
        updateGeoLocation(position.coords);
        dispatch({
          type: LayoutReducerActionType.updateGeoLocationLoading,
          payload: false,
        });
      },
      () => {
        clearTimeout(timeout);
        getLocationByApi();
      },
    );

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    processGeoLocation();
  }, []);

  return null;
};
